import type { ScreenerFilterWithIdAndTag } from '../../screener/StockScreener'
import {
    computeTurnDelta,
    ledgersSemanticallyEqual,
    mergeFilters,
    parseActiveConversationFilters,
    parseFiltersFromProto,
} from '../../screener/aiChat/conversationFilters'
import type { FilterCardStatus } from '../../screener/aiChat/types'
import type { AiConversationArtifact } from '../../../services/apis/aiChat'
import type { SurfaceApplyResult } from '../surfaceRegistry'

export type ScreenerLedgerState = {
    ledger: ScreenerFilterWithIdAndTag[]
    pendingLedgerApply: {
        delta: ScreenerFilterWithIdAndTag[]
        ledgerAfter: ScreenerFilterWithIdAndTag[]
    } | null
    pendingFiltersBuffer: ScreenerFilterWithIdAndTag[] | null
}

export function emptyScreenerLedgerState(): ScreenerLedgerState {
    return {
        ledger: [],
        pendingLedgerApply: null,
        pendingFiltersBuffer: null,
    }
}

export function filtersFromArtifacts(
    artifacts: AiConversationArtifact[] | undefined,
): ScreenerFilterWithIdAndTag[] | null {
    if (!Array.isArray(artifacts)) return null
    for (let i = artifacts.length - 1; i >= 0; i--) {
        const a = artifacts[i]
        if (a && a.kind === 'custom' && a.type === 'filter') {
            const data = a.data as Record<string, unknown> | undefined
            if (data?.turn_delta) {
                return parseFiltersFromProto(data.turn_delta)
            }
            return parseFiltersFromProto(a.data)
        }
    }
    return null
}

export function filterStatusFromArtifacts(artifacts: AiConversationArtifact[] | undefined): FilterCardStatus {
    if (!Array.isArray(artifacts)) return 'pending'
    for (let i = artifacts.length - 1; i >= 0; i--) {
        const a = artifacts[i]
        if (a && a.kind === 'custom' && a.type === 'filter') {
            const s = a.status?.toLowerCase()
            if (s === 'applied') return 'applied'
            if (s === 'undone') return 'undone'
            if (s === 'dismissed') return 'dismissed'
            return 'applied'
        }
    }
    return 'applied'
}

export function computePendingFilterUpdate(
    ledger: ScreenerFilterWithIdAndTag[],
    meta: Record<string, unknown>,
    fullFilters: ScreenerFilterWithIdAndTag[],
): { delta: ScreenerFilterWithIdAndTag[]; ledgerAfter: ScreenerFilterWithIdAndTag[] } | null {
    let mode: 'merge' | 'replace' = meta.mode === 'replace' ? 'replace' : 'merge'
    const parsedDelta = meta.turn_delta ? parseFiltersFromProto(meta.turn_delta) : null
    let delta = parsedDelta?.length
        ? parsedDelta
        : mode === 'replace'
          ? fullFilters
          : computeTurnDelta(ledger, fullFilters)

    if (!delta.length && mode === 'merge') {
        if (ledgersSemanticallyEqual(ledger, fullFilters)) {
            return null
        }
        delta = computeTurnDelta(ledger, fullFilters)
        if (!delta.length) {
            mode = 'replace'
            delta = fullFilters
        }
    }

    if (!delta.length) {
        return null
    }

    const ledgerAfter = mode === 'replace' ? [...fullFilters] : mergeFilters(ledger, delta)

    if (ledgersSemanticallyEqual(ledger, ledgerAfter)) {
        return null
    }

    return { delta, ledgerAfter }
}

export function applyServerLedger(
    raw: unknown,
    restoreScreenerFilters: (filters: ScreenerFilterWithIdAndTag[]) => void,
): ScreenerFilterWithIdAndTag[] {
    const ledger = parseActiveConversationFilters(raw)
    restoreScreenerFilters(ledger)
    return ledger
}

export function reconcileLedgerFromServer(
    convId: string,
    currentConvId: string | null,
    ledgerRef: ScreenerFilterWithIdAndTag[],
    restoreScreenerFilters: (filters: ScreenerFilterWithIdAndTag[]) => void,
    fetchConversation: (id: string) => Promise<{ activeConversationFilters?: unknown } | null>,
): Promise<ScreenerFilterWithIdAndTag[] | null> {
    if (currentConvId !== convId) return Promise.resolve(null)
    return fetchConversation(convId)
        .then((detail) => {
            if (currentConvId !== convId) return null
            if (detail?.activeConversationFilters == null) return null
            const serverLedger = parseActiveConversationFilters(detail.activeConversationFilters)
            if (ledgersSemanticallyEqual(ledgerRef, serverLedger)) return null
            restoreScreenerFilters(serverLedger)
            return serverLedger
        })
        .catch(() => null)
}

export function deriveTurnDomain(artifacts: AiConversationArtifact[] | undefined): 'chart' | 'screener' | null {
    if (!Array.isArray(artifacts)) return null
    for (const a of artifacts) {
        if (a?.kind === 'custom' && a.type === 'chart_command') return 'chart'
        if (a?.kind === 'custom' && a.type === 'filter') return 'screener'
    }
    return null
}

export function screenerFieldsFromMetadata(meta: Record<string, unknown>): string[] | null {
    const fields = meta.fields
    if (!Array.isArray(fields)) return null
    return fields.map((f) => String(f)).filter(Boolean)
}

export function screenerSortFromMetadata(meta: Record<string, unknown>): unknown | null {
    return meta.sort ?? null
}

export function screenerSurfaceApplyResult(): SurfaceApplyResult {
    return { applied: [], failed: [] }
}
